import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  UserPlus,
  BookOpen,
  MoreVertical,
  MessageSquare,
  Mail,
  DollarSign,
  Edit,
  Trash2,
  X,
  ChevronDown,
} from 'lucide-react';

interface Student {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  grade: string;
  status: 'active' | 'inactive';
  enrolledCourses: number;
  paymentStatus: 'paid' | 'pending' | 'overdue';
}

const mockStudents: Student[] = [
  { id: '1', name: 'Alice Johnson', email: 'alice@email.com', grade: '10th Grade', status: 'active', enrolledCourses: 5, paymentStatus: 'paid' },
  { id: '2', name: 'Bob Smith', email: 'bob@email.com', grade: '11th Grade', status: 'active', enrolledCourses: 4, paymentStatus: 'pending' },
  { id: '3', name: 'Catherine Lee', email: 'catherine@email.com', grade: '9th Grade', status: 'active', enrolledCourses: 6, paymentStatus: 'paid' },
  { id: '4', name: 'David Brown', email: 'david@email.com', grade: '12th Grade', status: 'inactive', enrolledCourses: 3, paymentStatus: 'overdue' },
  { id: '5', name: 'Emma Wilson', email: 'emma@email.com', grade: '10th Grade', status: 'active', enrolledCourses: 5, paymentStatus: 'paid' },
  { id: '6', name: 'Frank Martinez', email: 'frank@email.com', grade: '11th Grade', status: 'active', enrolledCourses: 4, paymentStatus: 'pending' },
];

export function SchoolStudentsTab() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [historyFilter, setHistoryFilter] = useState('current');
  const [showManageModal, setShowManageModal] = useState(false);
  const [contextMenuId, setContextMenuId] = useState<string | null>(null);

  const filteredStudents = mockStudents.filter(
    (student) =>
      student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map((s) => s.id));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedStudents((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const getPaymentBadgeColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'overdue':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-4">
      {/* Header Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search students..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 w-64 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          {/* History Filter */}
          <select
            value={historyFilter}
            onChange={(e) => setHistoryFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="current">Current Semester</option>
            <option value="previous">Previous Semester</option>
            <option value="all">All Time</option>
          </select>

          {/* View All */}
          <button className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
            View All
          </button>
        </div>

        <div className="flex items-center gap-3">
          {/* Manage Courses */}
          <button
            onClick={() => setShowManageModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <BookOpen className="w-4 h-4" />
            Manage Courses
          </button>

          {/* Add Student */}
          <button
            onClick={() => navigate('/admin/settings/invite-students')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700"
          >
            <UserPlus className="w-4 h-4" />
            Add Student
          </button>
        </div>
      </div>

      {/* Students Count */}
      <div className="text-sm text-gray-500">
        {filteredStudents.length} Students
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedStudents.length === filteredStudents.length && filteredStudents.length > 0}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Courses</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="w-24 px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredStudents.map((student) => (
              <tr key={student.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedStudents.includes(student.id)}
                    onChange={() => toggleSelect(student.id)}
                    className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-600 font-medium">
                      {student.name.split(' ').map((n) => n[0]).join('')}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{student.name}</p>
                      <p className="text-sm text-gray-500">{student.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{student.grade}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{student.enrolledCourses} courses</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPaymentBadgeColor(student.paymentStatus)}`}>
                    {student.paymentStatus.charAt(0).toUpperCase() + student.paymentStatus.slice(1)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    student.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {student.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={() => setShowManageModal(true)}
                      className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded"
                      title="Add to Course"
                    >
                      <BookOpen className="w-4 h-4" />
                    </button>
                    <button
                      className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                      title="Send Message"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setContextMenuId(contextMenuId === student.id ? null : student.id)}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {contextMenuId === student.id && (
                        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                          <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                            <Edit className="w-4 h-4" />
                            Edit Profile
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                            <BookOpen className="w-4 h-4" />
                            Manage Courses
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            Send Mail
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" />
                            Send Message
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            See Finance
                          </button>
                          <hr className="my-1" />
                          <button className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                            <Trash2 className="w-4 h-4" />
                            Delete Student
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Manage Students Modal */}
      {showManageModal && (
        <ManageStudentsModal onClose={() => setShowManageModal(false)} />
      )}
    </div>
  );
}

function ManageStudentsModal({ onClose }: { onClose: () => void }) {
  const [selectedCourse, setSelectedCourse] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewFilter, setViewFilter] = useState<'all' | 'enrolled'>('all');
  const [selectedStudents, setSelectedStudents] = useState<string[]>(['1', '3']);

  const courses = [
    { id: '1', name: 'UI For dummies' },
    { id: '2', name: 'Mathematics Advanced' },
    { id: '3', name: 'Physics Fundamentals' },
  ];

  const students = [
    { id: '1', name: 'Student Name', enrolled: true },
    { id: '2', name: 'Alice Johnson', enrolled: false },
    { id: '3', name: 'Brian Smith', enrolled: true },
    { id: '4', name: 'Catherine Lee', enrolled: false },
    { id: '5', name: 'Daniel Kim', enrolled: false },
    { id: '6', name: 'Eva Martinez', enrolled: false },
  ];

  const filteredStudents = students.filter((s) => {
    if (viewFilter === 'enrolled') return s.enrolled;
    return true;
  });

  const toggleStudent = (id: string) => {
    setSelectedStudents((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Attach Students to Courses</h2>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Course Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Choose Course</label>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Select a course</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>

          {/* Students Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Choose Students</label>
              <span className="text-sm text-gray-500">{selectedStudents.length} selected</span>
            </div>

            {/* Search and Filters */}
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search students..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  onClick={() => setViewFilter('all')}
                  className={`px-3 py-2 text-sm ${viewFilter === 'all' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700'}`}
                >
                  View All
                </button>
                <button
                  onClick={() => setViewFilter('enrolled')}
                  className={`px-3 py-2 text-sm ${viewFilter === 'enrolled' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700'}`}
                >
                  In this Course
                </button>
              </div>
            </div>

            {/* Students List */}
            <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
              {filteredStudents.map((student) => (
                <label
                  key={student.id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={selectedStudents.includes(student.id)}
                    onChange={() => toggleStudent(student.id)}
                    className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                  />
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 text-sm font-medium">
                    {student.name.split(' ').map((n) => n[0]).join('')}
                  </div>
                  <span className="text-sm text-gray-900">{student.name}</span>
                  {student.enrolled && (
                    <span className="ml-auto text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded">Enrolled</span>
                  )}
                </label>
              ))}
            </div>

            {/* Info Banner */}
            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                Selecting students here will enroll them in the selected course. Students can be enrolled in the Academics section as well.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            Reset
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
